import { EntityField, EntityFieldDataStore, SearchResponse } from '@skinsoft/data-store';
import localforage from 'localforage';
import _ from 'lodash';
import { get, merge as lodashMerge } from 'lodash';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';
import { CACHE_KEYS } from '../cache/search-type-cache-key-mapping';

const GLOBAL_SYNC_STATS_CACHE_KEY = 'global-sync-stats';

declare const FALLBACK_LOCALE;

const offlineDataCache = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-offline-data-cache',
    storeName: 'runtime_cache',
    version: 2.0,
});

export interface DataSynchronizerStats {
    maxNbToSynchronize: number,
    currentNbSychronized: number,
    lastSynchroDuration: number | null,
    lastSynchroDate: number | null,
    lastSynchroStatus: DataSynchronisationStatus | null
}

export enum DataSynchronisationStatus {
    SUCCESS = "success",
    ABORTED = "aborted",
    ERROR = "error",
    NONE = "none"
}

export class OfflineDataFetcher<T extends any, S extends any = T> {

    private get statsCacheKey() {
        return `${this.cacheKey}-stats`
    }

    public static isTemporaryId(id: string) {
        return id && typeof id === 'string' ? id.startsWith('_') : false;
    }

    public static async getLastGlobalSyncStats(): Promise<DataSynchronizerStats> {
        return await offlineDataCache.getItem(GLOBAL_SYNC_STATS_CACHE_KEY) as DataSynchronizerStats;
    }

    public static async saveGlobalSyncStats(syncStats: DataSynchronizerStats) {
        await offlineDataCache.setItem(GLOBAL_SYNC_STATS_CACHE_KEY, syncStats)
    }

    public static async isSyncError(): Promise<boolean> {
        const stats = await this.getLastGlobalSyncStats();
        if (stats) {
            return stats.lastSynchroStatus === DataSynchronisationStatus.ERROR
                || stats.lastSynchroStatus === DataSynchronisationStatus.ABORTED;
        }
        return true;
    }

    private lastSynchroStats: DataSynchronizerStats = null as any;

    private cacheKey: string = "";

    public constructor(cacheKey: string) {
        this.cacheKey = cacheKey;
        this.fetchLastSynchroStats()
    }

    public getLastSynchroStats() {
        return this.lastSynchroStats;
    }

    public clear() {
        offlineDataCache.setItem(this.cacheKey, [])
    }

    public saveStats(synchroStats: DataSynchronizerStats) {
        offlineDataCache.setItem(this.statsCacheKey, synchroStats);
        this.lastSynchroStats = synchroStats;
    }

    /**
     * Updates the value in cache having the provided id with the given entity object
     * Appends it to existing values if it doesn't exist in the cache yet
     * @param id 
     * @param entity 
     */
    public async updateOne(id: string, entity: T) {
        const data: T[] = await offlineDataCache.getItem(this.cacheKey);
        if (data) {
            const matchingIndex = data.findIndex(theEntity => theEntity.id === id);
            if (matchingIndex >= 0) {
                data[matchingIndex] = entity;
            }
            else {
                data.push(entity);
            }
            offlineDataCache.setItem(this.cacheKey, data);
        }
    }

    public fetchOneFromId(id: string): Observable<T> {
        return from(offlineDataCache.getItem<any[]>(this.cacheKey))
            .pipe(
                switchMap(
                    data => {
                        if (data && data.length > 0) {
                            const filteredOnId = data.find(
                                result => {
                                    return this.idMatcher(result, id);
                                }
                            )
                            return of(filteredOnId)
                        }
                        return EMPTY;
                    }
                ),
                first()
            )
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean): Observable<T> {
        return from(offlineDataCache.getItem<any[]>(this.cacheKey))
            .pipe(
                switchMap(
                    data => {
                        if (data) {
                            return of(data.find(predicate))
                        }
                        else {
                            return throwError("No data in cache for " + this.cacheKey)
                        }
                    })
            )
    }

    /**
     * Tries to reproduce the behaviour of online search on cache data
     * @param query 
     */
    public searchAll(query: any, locallyCreatedObjects: S[], locallyUpdatedObjects: S[], onlyOnLocallyCreated: boolean = false, language: string = FALLBACK_LOCALE): Observable<SearchResponse<S>> {
        // Filter and sort only locally created data
        if (onlyOnLocallyCreated) {
            return from(this.applyFiltersAndSortsOnData(locallyCreatedObjects, query, language)).pipe(
                switchMap(
                    (value: any) => {
                        return of({
                            allData: value.reducedData,
                            results: [],
                            totalSize: value.totalSize,
                        });
                    }
                )
            )
        }
        // Filter and sort data in offline cache AND locally created entities AND locally updated entities
        else {
            return from(offlineDataCache.getItem<S[]>(this.cacheKey))
                .pipe(
                    switchMap(data => {
                        return of(this.mergeUpdateCacheDataToSearchResults(data, locallyUpdatedObjects))
                    }),
                    switchMap(
                        async (data: S[]) => {
                            const results: S[] = data;
                            if (locallyCreatedObjects) {
                                results.push(...locallyCreatedObjects);
                            }

                            const { reducedData, totalSize } = await this.applyFiltersAndSortsOnData(results, query, language);

                            return {
                                allData: reducedData,
                                results: [],
                                totalSize,
                            };
                        }
                    )
                )
        }
    }

    public async searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: string, language: string = FALLBACK_LOCALE) {
        const cacheKey = CACHE_KEYS[types[0]]
        const cachedData: T[] = await offlineDataCache.getItem(cacheKey || this.cacheKey);

        let filteredData: T[] = cachedData

        // Filter values whose 'esPath' value matches 'defaultFilter'
        if (defaultFilter && esPath) {
            filteredData = filteredData.filter(
                value => {
                    const sanitizedEsPath = esPath.substring(0, esPath?.lastIndexOf('.ref')); // eliminating .ref from indexation if needed
                    return _.get(value, sanitizedEsPath) === defaultFilter;
                }
            )
        }

        // Filter values mathching some ids
        if (filterIds) {
            filteredData = filteredData.filter(
                value => {
                    return filterIds && filterIds.length > 0 ? filterIds.includes(value.id) : true;
                }
            )
        }

        if (filterText) {
            filteredData = cachedData.filter(
                value => {
                    return this.titleMatcher(value, filterText, language);
                }
            )
        }

        const reshapedData = filteredData.map(
            value => {
                const reshapedValue = {
                    genericEntityTitle:
                    {
                        translatedString: {}
                    },
                    id: value.id,
                }
                _.set(reshapedValue,
                    `genericEntityTitle.translatedString.${language}`,
                    _.get(value, `indexTitle.translatedString.${language}` || "..."));
                return reshapedValue;
            }
        )

        const sortedData = reshapedData
            .sort(
                (titleA, titleB) => {
                    const titleAStr = get(titleA, `genericEntityTitle.translatedString.${language}`) || "...";
                    const titleBStr = get(titleB, `genericEntityTitle.translatedString.${language}`) || "...";

                    return titleAStr.localeCompare(titleBStr, language);
                }
            )
        const clampedData = sortedData.slice(pageNumber * size, pageNumber * size + size);

        return {
            results: clampedData,
            totalSize: sortedData.length,
        };
    }

    public async append(dataToAppend: S[]) {
        let cachedData: S[] = await offlineDataCache.getItem(this.cacheKey);
        if (!cachedData) {
            cachedData = []
        }
        cachedData.push(...dataToAppend)
        await offlineDataCache.setItem(this.cacheKey, cachedData);
    }

    /**
     * Override this class to customize search by entity type
     * @param entity 
     * @param queryText 
     */
    protected async queryTextFilter(entity: S, queryText: string, locale: string): Promise<boolean> {
        return true;
    }

    protected idMatcher(entity: T, id: string) {
        return _.get(entity, 'id') === id
    }

    protected titleMatcher(entity: T, title: string, locale: string) {
        return _.get(entity, `indexTitle.translatedString[${locale}]`) === title;
    }

    /**
     * Merges entities with their equivalent in cache, the correspondance is done by the 'id' field
     * @param fetchedData 
     * @param locallyUpdatedData 
     */
    private mergeUpdateCacheDataToSearchResults(fetchedData: S[], locallyUpdatedData: S[]) {
        for (let fetchedEntity of fetchedData) {
            const cachedUpdatedValue = locallyUpdatedData.filter(data => data.id === fetchedEntity.id)
            if (cachedUpdatedValue) {
                fetchedEntity = this.mergeCacheToEntity(fetchedEntity, cachedUpdatedValue[0])
            }
        }
        return fetchedData;
    }

    /**
     * Merges cache data with an entity to take all modifications into account
     * @param entity 
     * @param cachedValue 
     */
    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }

    /**
     * Builds a predicate from a search criteria 
     * @param criteria 
     * @param entityField 
     */
    private buildPredicateFromEntityField(criteria, entityField) {
        return (entity) => {
            const path = `${entityField.path}`;
            const value = get(entity, `${path}.name`) || get(entity, path)

            switch (entityField.type.type) {
                case "List":
                    switch (criteria.value.type) {
                        case "ref":
                            return value.includes(criteria.value.refId);
                        default:
                            return value.includes(criteria.value.refId);
                    }
                case "LifecycleState":
                    return criteria.value.text.includes(value)
                case "LinkableArchiveItem":
                    return value.linkableItem === criteria.value.refId
                default:
                    switch (criteria.value.type) {
                        case "ref":
                            return value === criteria.value.refId;
                        default:
                            return value === criteria.value.text
                    }

            }
        }
    }

    /**
     * Applies the necessary filters and sorts to given data, according to query options
     * @param data 
     * @param query 
     */
    private async applyFiltersAndSortsOnData(data: S[], query: any, language: string = FALLBACK_LOCALE): Promise<{ reducedData: S[], totalSize: number }> {
        // filtering on ids
        if (query.filteredIds) {
            if (data.length > 0) {
                data = data.filter(
                    item => query.filteredIds.includes(item.id)
                )
            }
        }

        // Filtering on property value
        if (query.pathAndValueToCheck) {
            data = data.filter(
                item => query.pathAndValueToCheck.value.includes(get(item, query.pathAndValueToCheck.pathToValue + ".name"))
            )
        }

        // Managing query text
        if (query.queryText) {

            // data =
            await Promise.all(data.map(
                async value => {
                    value.tempQueryTextFilter = await this.queryTextFilter(value, query.queryText, language);
                    return value;
                }
            ))

            data = data.filter(
                value => {
                    const keep = value.tempQueryTextFilter;
                    delete value.tempQueryTextFilter;
                    return keep;
                }
            )
        }

        // Managing advanced search
        if (query.advancedSearches && query.advancedSearches.length > 0) {
            const leftCriteria = query.advancedSearches[0].leftCriteria;
            const rightCriteria = query.advancedSearches[0].rightCriteria;
            const operator = query.advancedSearches[0].linkOperator;

            let leftPredicate = entity => true;
            let rightPredicate = entity => true;

            if (leftCriteria.operator === "equals") {
                const entityFieldId = leftCriteria.entityField;
                const entityField: EntityField = await EntityFieldDataStore.getInstance().fetchOne(entityFieldId)
                    .toPromise();
                leftPredicate = this.buildPredicateFromEntityField(leftCriteria, entityField)
            }

            if (operator && rightCriteria) {
                const entityFieldId = rightCriteria.entityField;
                const entityField: EntityField = await EntityFieldDataStore.getInstance().fetchOne(entityFieldId)
                    .toPromise();
                rightPredicate = this.buildPredicateFromEntityField(rightCriteria, entityField)
            }

            switch (operator) {
                case "AND":
                    // data = data.filter(leftPredicate && rightPredicate)
                    data = data.filter(entity => leftPredicate(entity) && rightPredicate(entity))
                    break;
                case "OR":
                    data = data.filter(entity => leftPredicate(entity) || rightPredicate(entity))
                    break;
                default:
                    data = data.filter(leftPredicate)
                    break;

            }
        }

        // Manage sorting
        if (query.sortObject) {
            const sortEntityField = await EntityFieldDataStore.getInstance().fetchOne(query.sortObject.entityFields[0])
                .pipe(first())
                .toPromise()

            // Resolving temporay sort key
            const dataWithSortingkey = await Promise.all(data.map(
                async value => {
                    if (!value) {
                        value = {} as unknown as S
                    }
                    value.tempSortKey = await EntityFieldDataStore.getValueFromEntityField(value, sortEntityField, language) || "";
                    return value;
                }
            ))

            data = dataWithSortingkey
                .sort(
                    (value1, value2) => {
                        return query.sortObject.sortAsc ?
                            value1.tempSortKey.localeCompare(value2.tempSortKey, language) :
                            value2.tempSortKey.localeCompare(value1.tempSortKey, language);
                    }
                )
                .map(
                    value => {
                        delete value.tempSortKey;
                        return value;
                    }
                )
        }

        const totalSize = data.length;
        // Pagination options
        if (query.pageSize) {
            if (query.page) {
                const startIndex = query.page * query.pageSize;
                data = data.slice(startIndex, startIndex + query.pageSize);
            } else {
                data.splice(query.pageSize);
            }
        }

        return { reducedData: data, totalSize };
    }

    private async fetchLastSynchroStats() {
        this.lastSynchroStats = await offlineDataCache.getItem(this.statsCacheKey) as DataSynchronizerStats
    }
}
